/**
 * User-drawn smart objects on the Review canvas (Phase 3).
 * Geometry stored in PDF page space; canvas coords via pdf.js viewport.
 */

import { measureGeometryPdf, pdfAreaToRealSqFeet, pdfPointsToRealFeet } from './drawingMeasurements.js';
import {
  arcLengthPdf,
  arcPdfVertices,
  ARC_STYLES,
  buildBulgeArcGeometry,
  buildCircularArcGeometry,
  hitTestArc,
  hitTestCircle,
  normalizeArcGeometry,
  paintArcGeometry,
} from './arcGeometry.js';
import {
  defaultStyleForFillableMarkup,
  isFillableMarkupGeometry,
  paintFillableStyle,
  resolveMarkupStyle,
  strokeMarkupPath,
} from './markupStyle.js';

export { ARC_STYLES } from './arcGeometry.js';

export const DRAW_TOOLS = {
  SELECT: 'select',
  LINE: 'line',
  POLYLINE: 'polyline',
  RECTANGLE: 'rectangle',
  POLYGON: 'polygon',
  ARC: 'arc',
  CIRCLE: 'circle',
  FREEFORM: 'freeform',
};

export const DRAW_TOOL_OPTIONS = [
  { id: DRAW_TOOLS.SELECT, label: 'Select', hint: 'Select and edit objects' },
  { id: DRAW_TOOLS.LINE, label: 'Line', hint: 'Single segment' },
  { id: DRAW_TOOLS.POLYLINE, label: 'Polyline', hint: 'Click points · double-click to finish' },
  { id: DRAW_TOOLS.RECTANGLE, label: 'Rectangle', hint: 'Click and drag' },
  { id: DRAW_TOOLS.POLYGON, label: 'Polygon', hint: 'Click corners · click near start to close' },
  { id: DRAW_TOOLS.FREEFORM, label: 'Freeform', hint: 'Drag to draw a closed shape' },
  { id: DRAW_TOOLS.ARC, label: 'Arc', hint: 'Circular: center, start, end · Bulge: start, end, control' },
  { id: DRAW_TOOLS.CIRCLE, label: 'Circle', hint: 'Click center, then radius point' },
];

const USER_STROKE = '#e97024';
const USER_FILL = 'rgba(233, 112, 36, 0.15)';

export function isUserDrawnComponent(comp) {
  return comp?.source === 'user' || comp?.objectSource === 'user_drawn';
}

export function markupStorageKey(projectId, requirementId, sheetId) {
  return `codiii-review-markups:${projectId || ''}:${requirementId || ''}:${sheetId || ''}`;
}

export function loadUserMarkups(key) {
  if (!key || typeof localStorage === 'undefined') return [];
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveUserMarkups(key, markups) {
  if (!key || typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(key, JSON.stringify(markups));
  } catch {
    // quota or private mode
  }
}

export function newUserMarkupId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return `user-${crypto.randomUUID()}`;
  }
  return `user-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function geometryTypeForTool(tool) {
  if (tool === DRAW_TOOLS.RECTANGLE) return 'bbox';
  if (tool === DRAW_TOOLS.LINE) return 'polyline';
  if (tool === DRAW_TOOLS.ARC) return 'arc';
  if (tool === DRAW_TOOLS.CIRCLE) return 'circle';
  if (tool === DRAW_TOOLS.POLYGON || tool === DRAW_TOOLS.FREEFORM) return 'polygon';
  return 'polyline';
}

/** Normalize legacy markup geometry loaded from storage. */
export function normalizeUserMarkupGeometry(geometry) {
  if (!geometry) return geometry;
  if (geometry.type === 'arc') return normalizeArcGeometry(geometry);
  return geometry;
}

export function normalizeUserMarkup(component) {
  if (!component?.geometry) return component;
  const geometry = normalizeUserMarkupGeometry(component.geometry);
  if (geometry === component.geometry) return component;
  return {
    ...component,
    geometry,
    measurements: measureUserGeometry(geometry),
  };
}

function bboxFromPoints(points) {
  if (!points?.length) return null;
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  for (const [x, y] of points) {
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  }
  return [x0, y0, x1 - x0, y1 - y0];
}

/** Canvas overlay coords → PDF page points (bottom-left origin). */
export function canvasToPdfPoint(cx, cy, viewport) {
  if (!viewport) return [cx, cy];
  const [pdfX, pdfY] = viewport.convertToPdfPoint(cx, cy);
  return [pdfX, pdfY];
}

/** PDF page points → canvas overlay coords. */
export function pdfPointToCanvas(px, py, viewport) {
  if (!viewport) return { x: px, y: py };
  const [sx, sy] = viewport.convertToViewportPoint(px, py);
  return { x: sx, y: sy };
}

export function pdfPointsToCanvas(points, viewport) {
  return (points || []).map(([px, py]) => pdfPointToCanvas(px, py, viewport));
}

export function measureUserGeometry(geometry, { scaleRatio = null } = {}) {
  const geom = normalizeUserMarkupGeometry(geometry);
  const type = geom?.type || 'polyline';
  const out = {};

  let lengthPt = 0;
  let areaSqPt = null;

  if (type === 'arc') {
    lengthPt = arcLengthPdf(geom);
  } else {
    const measured = measureGeometryPdf(geom);
    lengthPt = measured.lengthPt;
    areaSqPt = measured.areaSqPt;
  }

  out.lengthPt = lengthPt;
  if (areaSqPt != null) {
    out.areaSqPt = areaSqPt;
    out.areaSqFt = scaleRatio ? pdfAreaToRealSqFeet(areaSqPt, scaleRatio) : null;
  }
  if (lengthPt > 0) {
    out.lengthFt = scaleRatio ? pdfPointsToRealFeet(lengthPt, scaleRatio) : null;
  }

  return out;
}

export function createUserComponent({
  tool,
  pdfPoints,
  arcStyle = ARC_STYLES.CIRCULAR,
  pageIndex = 0,
  sheetId = '',
  hatId = 'architect',
  kind = '',
}) {
  const type = geometryTypeForTool(tool);
  let geometry;

  if (tool === DRAW_TOOLS.ARC && pdfPoints.length >= 3) {
    geometry = arcStyle === ARC_STYLES.BULGE
      ? buildBulgeArcGeometry(pdfPoints)
      : buildCircularArcGeometry(pdfPoints[0], pdfPoints[1], pdfPoints[2]);
    geometry.bbox = bboxFromPoints(arcPdfVertices(geometry));
  } else if (tool === DRAW_TOOLS.CIRCLE && pdfPoints.length >= 2) {
    const center = pdfPoints[0];
    const radius = Math.hypot(
      pdfPoints[1][0] - center[0],
      pdfPoints[1][1] - center[1],
    );
    geometry = {
      type: 'circle',
      center,
      radius,
      points: [],
      bbox: [center[0] - radius, center[1] - radius, 2 * radius, 2 * radius],
    };
  } else if (tool === DRAW_TOOLS.FREEFORM) {
    geometry = {
      type: 'polygon',
      closed: true,
      points: pdfPoints,
      bbox: bboxFromPoints(pdfPoints),
    };
  } else {
    geometry = {
      type,
      points: type === 'bbox' ? [] : pdfPoints,
    };
    if (type === 'bbox' && pdfPoints.length >= 2) {
      const xs = pdfPoints.map((p) => p[0]);
      const ys = pdfPoints.map((p) => p[1]);
      geometry.bbox = [
        Math.min(...xs),
        Math.min(...ys),
        Math.max(...xs) - Math.min(...xs),
        Math.max(...ys) - Math.min(...ys),
      ];
    } else {
      geometry.bbox = bboxFromPoints(pdfPoints);
    }
    if (type === 'polygon' && tool === DRAW_TOOLS.POLYGON) {
      geometry.closed = true;
    }
  }

  const measurements = measureUserGeometry(geometry);
  const fillable = isFillableMarkupGeometry(geometry, tool);

  return {
    id: newUserMarkupId(),
    source: 'user',
    objectSource: 'user_drawn',
    drawTool: tool,
    mappingStatus: 'pending_user_confirm',
    pageIndex,
    sheetId,
    kind: kind || '',
    label: kind || 'User markup',
    taxonomyAxis: 'user_markup',
    hatIds: hatId ? [hatId] : [],
    geometry,
    measurements,
    ...(fillable ? { style: defaultStyleForFillableMarkup() } : {}),
    createdAt: new Date().toISOString(),
  };
}

function drawOpenPolyline(ctx, canvasPoints, style) {
  if (!canvasPoints.length) return;
  ctx.beginPath();
  ctx.moveTo(canvasPoints[0].x, canvasPoints[0].y);
  for (let i = 1; i < canvasPoints.length; i += 1) {
    ctx.lineTo(canvasPoints[i].x, canvasPoints[i].y);
  }
  strokeMarkupPath(ctx, style);
}

function drawClosedFillableShape(ctx, canvasPoints, style) {
  if (!canvasPoints.length) return;
  ctx.beginPath();
  ctx.moveTo(canvasPoints[0].x, canvasPoints[0].y);
  for (let i = 1; i < canvasPoints.length; i += 1) {
    ctx.lineTo(canvasPoints[i].x, canvasPoints[i].y);
  }
  ctx.closePath();
  paintFillableStyle(ctx, style);
  strokeMarkupPath(ctx, style);
}

export function paintUserMarkup(ctx, component, viewport, { selected = false } = {}) {
  if (!ctx || !viewport || !component?.geometry) return;
  const geometry = normalizeUserMarkupGeometry(component.geometry);
  const fillable = isFillableMarkupGeometry(geometry, component.drawTool);
  const style = fillable
    ? resolveMarkupStyle(component, { selected })
    : resolveMarkupStyle({ style: { strokeColor: USER_STROKE, strokeWidth: 2 } }, { selected });
  const lineStroke = style.strokeColor;
  const lineWidth = style.strokeWidth;
  const legacyFill = selected ? 'rgba(37, 99, 235, 0.12)' : USER_FILL;

  if (geometry.type === 'arc') {
    paintArcGeometry(ctx, geometry, viewport, { stroke: lineStroke, width: lineWidth });
    return;
  }

  if (geometry.type === 'circle') {
    if (!geometry?.center || !geometry.radius) return;
    const center = pdfPointToCanvas(geometry.center[0], geometry.center[1], viewport);
    const edge = pdfPointToCanvas(
      geometry.center[0] + geometry.radius,
      geometry.center[1],
      viewport,
    );
    const r = Math.hypot(edge.x - center.x, edge.y - center.y);
    ctx.beginPath();
    ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
    if (fillable) {
      paintFillableStyle(ctx, style);
    } else {
      ctx.fillStyle = legacyFill;
      ctx.fill();
    }
    ctx.beginPath();
    ctx.arc(center.x, center.y, r, 0, Math.PI * 2);
    strokeMarkupPath(ctx, style);
    return;
  }

  if (geometry.type === 'bbox' && Array.isArray(geometry.bbox) && geometry.bbox.length >= 4) {
    const [x, y, w, h] = geometry.bbox;
    const p0 = pdfPointToCanvas(x, y, viewport);
    const p1 = pdfPointToCanvas(x + w, y + h, viewport);
    const rx = Math.min(p0.x, p1.x);
    const ry = Math.min(p0.y, p1.y);
    const rw = Math.abs(p1.x - p0.x);
    const rh = Math.abs(p1.y - p0.y);
    ctx.beginPath();
    ctx.rect(rx, ry, rw, rh);
    if (fillable) {
      paintFillableStyle(ctx, style);
    } else {
      ctx.fillStyle = legacyFill;
      ctx.fill();
    }
    ctx.beginPath();
    ctx.rect(rx, ry, rw, rh);
    strokeMarkupPath(ctx, style);
    return;
  }

  const canvasPts = pdfPointsToCanvas(geometry.points, viewport);
  if (!canvasPts.length) return;
  const closed = geometry.type === 'polygon'
    && (geometry.closed || component.drawTool === DRAW_TOOLS.FREEFORM);
  if (closed && fillable) {
    drawClosedFillableShape(ctx, canvasPts, style);
  } else {
    drawOpenPolyline(ctx, canvasPts, style);
  }
}

export function hitTestUserMarkup(canvasX, canvasY, component, viewport) {
  if (!component?.geometry || !viewport) return false;
  const geometry = normalizeUserMarkupGeometry(component.geometry);

  if (geometry.type === 'arc') {
    return hitTestArc(canvasX, canvasY, geometry, viewport);
  }

  if (geometry.type === 'circle') {
    return hitTestCircle(canvasX, canvasY, geometry, viewport);
  }

  if (geometry.type === 'bbox' && Array.isArray(geometry.bbox)) {
    const [x, y, w, h] = geometry.bbox;
    const p0 = pdfPointToCanvas(x, y, viewport);
    const p1 = pdfPointToCanvas(x + w, y + h, viewport);
    const rx = Math.min(p0.x, p1.x);
    const ry = Math.min(p0.y, p1.y);
    const rw = Math.abs(p1.x - p0.x);
    const rh = Math.abs(p1.y - p0.y);
    return canvasX >= rx && canvasX <= rx + rw && canvasY >= ry && canvasY <= ry + rh;
  }

  const pts = pdfPointsToCanvas(geometry.points, viewport);
  const threshold = 8;
  for (let i = 0; i < pts.length - 1; i += 1) {
    const dist = pointToSegmentDist(canvasX, canvasY, pts[i], pts[i + 1]);
    if (dist <= threshold) return true;
  }
  const isClosedPolygon = geometry.type === 'polygon'
    && (geometry.closed || component.drawTool === DRAW_TOOLS.FREEFORM);
  if (isClosedPolygon && pts.length >= 3) {
    return pointInPolygon(canvasX, canvasY, pts);
  }
  if (pts.length >= 2) {
    const dist = pointToSegmentDist(canvasX, canvasY, pts[0], pts[pts.length - 1]);
    if (dist <= threshold) return true;
  }
  return false;
}

function pointToSegmentDist(px, py, a, b) {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const lenSq = dx * dx + dy * dy;
  if (lenSq === 0) return Math.hypot(px - a.x, py - a.y);
  let t = ((px - a.x) * dx + (py - a.y) * dy) / lenSq;
  t = Math.max(0, Math.min(1, t));
  const projX = a.x + t * dx;
  const projY = a.y + t * dy;
  return Math.hypot(px - projX, py - projY);
}

function pointInPolygon(x, y, pts) {
  let inside = false;
  for (let i = 0, j = pts.length - 1; i < pts.length; j = i, i += 1) {
    const xi = pts[i].x;
    const yi = pts[i].y;
    const xj = pts[j].x;
    const yj = pts[j].y;
    const intersect = ((yi > y) !== (yj > y))
      && (x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-9) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

export function closeThresholdPx(viewport) {
  return 12;
}

export function isNearPoint(a, b, threshold = 12) {
  return Math.hypot(a.x - b.x, a.y - b.y) <= threshold;
}
